import { Box, Typography } from "@mui/material";
import { tokens } from "../../../theme";
import { useRef, useState } from "react";
import { KeyboardArrowDown } from "@mui/icons-material";
import UpcomingAuditsCard, { AuditEntry } from "./UpcomingAuditsCard";

type AuditStatus = "overdue" | "in_danger" | "safe";

const STATUS_CONFIG: Record<
  AuditStatus,
  { label: string; borderColor: string }
> = {
  overdue: { label: "OVER DUE", borderColor: "#E11D48" },
  in_danger: { label: "IN DANGER", borderColor: "#f59e0b" },
  safe: { label: "SAFE", borderColor: "#10b981" },
};

interface UpcomingAuditsStatusColumnProps {
  status: AuditStatus;
  entries: AuditEntry[];
  colors: ReturnType<typeof tokens>;
  loading?: boolean;
  onCardClick: (entry: AuditEntry) => void;
}

const UpcomingAuditsStatusColumn = ({
  status,
  entries,
  colors,
  loading,
  onCardClick,
}: UpcomingAuditsStatusColumnProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);

  const cfg = STATUS_CONFIG[status];
  const CARD_HEIGHT = 72;

  const handleScroll = () => {
    if (!listRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    setScrolledToEnd(scrollTop + clientHeight >= scrollHeight - 4);
  };

  const scrollToNext = () => {
    if (!listRef.current) return;
    listRef.current.scrollBy({ top: CARD_HEIGHT, behavior: "smooth" });
  };

  const hiddenCount = Math.max(0, entries.length - 3);

  const bounceKeyframe = {
    "@keyframes bounce": {
      "0%, 100%": { transform: "translateY(0)" },
      "50%": { transform: "translateY(-2px)" },
    },
  };

  return (
    <Box
      sx={{
        minWidth: { xs: 200, sm: 240, md: 260 },
        maxWidth: { xs: 200, sm: 240, md: 260 },
        borderTop: `1.5px solid ${cfg.borderColor}`,
        borderLeft: `1.5px solid ${cfg.borderColor}`,
        borderRight: `1.5px solid ${cfg.borderColor}`,
        borderBottom: loading
          ? `4px solid transparent`
          : `4px solid ${cfg.borderColor}`,
        backgroundImage: loading
          ? `linear-gradient(${colors.primary[800]}, ${colors.primary[800]}), linear-gradient(90deg, ${cfg.borderColor}20, ${cfg.borderColor}, ${cfg.borderColor}20)`
          : "none",
        backgroundOrigin: loading ? "border-box" : "unset",
        backgroundClip: loading ? "padding-box, border-box" : "unset",
        backgroundSize: loading ? "200% auto" : "unset",
        animation: loading ? "shimmer 1.5s linear infinite" : "none",
        "@keyframes shimmer": {
          "0%": { backgroundPosition: "-200% center" },
          "100%": { backgroundPosition: "200% center" },
        },
        borderRadius: "30px",
        p: 2,
        backgroundColor: colors.primary[800],
        flexShrink: 0,
        position: "relative",
      }}
    >
      {/* Header */}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.75rem",
          letterSpacing: "0.08em",
          color: cfg.borderColor,
          mb: 1.5,
        }}
      >
        {cfg.label}
      </Typography>

      {/* Cards */}
      <Box
        ref={listRef}
        onScroll={handleScroll}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          maxHeight: 3 * 64 + 2 * 8,
          overflowY: "auto",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {entries.map((entry) => (
          <UpcomingAuditsCard
            key={entry.id}
            entry={entry}
            colors={colors}
            onClick={() => onCardClick(entry)}
          />
        ))}
        {entries.length === 0 && (
          <Typography
            sx={{
              fontSize: "0.8rem",
              color: colors.primary[700],
              textAlign: "center",
              py: 2,
            }}
          >
            {loading ? "Loading..." : "No entries"}
          </Typography>
        )}
      </Box>

      {/* Scroll indicator */}
      {hiddenCount > 0 && !scrolledToEnd && (
        <Box
          onClick={scrollToNext}
          sx={{
            ...bounceKeyframe,
            position: "absolute",
            bottom: 4,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2,
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            animation: "bounce 1.2s ease-in-out infinite",
          }}
        >
          <KeyboardArrowDown
            sx={{ fontSize: 18, color: cfg.borderColor, mt: "-6px" }}
          />
          <KeyboardArrowDown
            sx={{
              fontSize: 18,
              color: cfg.borderColor,
              mt: "-10px",
              opacity: 0.5,
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default UpcomingAuditsStatusColumn;
